// Tiny dependency-free CSV export helpers (RFC 4180).
//
// `toCsv` turns an array of row objects into a CSV string given an explicit
// column spec (so header order + labels are stable and don't depend on object
// key order). `downloadCsv` triggers a browser download via a Blob object URL.

export interface CsvColumn<T = Record<string, unknown>> {
  /** Property key to read from each row. */
  key: keyof T & string;
  /** Human-readable header for this column. */
  header: string;
}

/**
 * Escape a single field per RFC 4180: wrap in double quotes when the value
 * contains a comma, double-quote, CR or LF, and double any embedded quotes.
 * `null`/`undefined` become empty strings.
 */
function escapeField(value: unknown): string {
  if (value === null || value === undefined) return '';
  const str = String(value);
  if (/[",\r\n]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

/** Build an RFC-4180 CSV string (CRLF line endings) from rows + column spec. */
export function toCsv<T extends Record<string, unknown>>(
  rows: T[],
  columns: CsvColumn<T>[],
): string {
  const headerLine = columns.map((c) => escapeField(c.header)).join(',');
  const dataLines = rows.map((row) =>
    columns.map((c) => escapeField(row[c.key])).join(','),
  );
  return [headerLine, ...dataLines].join('\r\n');
}

/**
 * Trigger a client-side download of `csv` as `filename`. Prepends a UTF-8 BOM
 * so Excel detects the encoding and renders accented characters correctly.
 */
export function downloadCsv(filename: string, csv: string): void {
  const BOM = '﻿';
  const blob = new Blob([BOM + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  // Release the object URL on the next tick so the download can start first.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
